import { LockOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import mergeClassNames from "common/mergeClassNames";
import AttachmentCell from "components/atoms/AttachmentCell";
import React, { useCallback, useMemo } from "react";
import FormPhoneDisplayField from "../FormPhoneDisplayField";
import SchemaFieldTitle from "../SchemaFieldTitle";

interface IFormFieldReadOnlyProps {
    name: string;
    description?: string;
    classifiers?: IDataTag[];
    isRequired?: boolean;
    isPII?: boolean;
    tooltip?: string;
    label?: React.ReactNode;
    dataType?: string;
    children?: React.ReactNode | React.ReactNode[];
    fieldError?: IValidationError;
}

const isAttachmentData = (value: any): boolean => {
    if (!value) return false;
    try {
        let parsed: any;
        if (typeof value === "string") {
            if (value.trim().startsWith("[") || value.trim().startsWith("{")) {
                parsed = JSON.parse(value);
            } else {
                return false;
            }
        } else {
            parsed = value;
        }
        const items = Array.isArray(parsed) ? parsed : [parsed];
        return items.length > 0 && items.every((item: any) => item && typeof item === "object" && (item.key || item.name || item.originalName));
    } catch {
        return false;
    }
};

export default function FormFieldReadOnly({ name, description, classifiers, isPII, isRequired, children, label, dataType, fieldError }: IFormFieldReadOnlyProps) {
    const getAttachmentValue = useCallback(() => {
        if (children !== null && children !== undefined && (typeof children === "string" || typeof children === "object")) {
            return children;
        }
        if (React.isValidElement(children)) {
            const props = children.props as any;
            return props?.value ?? props?.children ?? null;
        }
        if (Array.isArray(children) && children.length === 1) {
            return children[0];
        }
        return children;
    }, [children]);

    const isPhone = useMemo(() => dataType === "phone", [dataType]);
    const isAttachmentFieldByType = useMemo(() => dataType && ["attachment", "image", "document"].includes(dataType), [dataType]);
    const isAttachmentFieldByData = useMemo(() => isAttachmentData(getAttachmentValue()), [getAttachmentValue]);

    return (
        <div className="mb-4 flex w-full flex-col gap-2">
            <div className="flex items-center gap-1">
                {isRequired && <p className="text-red-500">*</p>}
                <SchemaFieldTitle name={name} tags={classifiers} orientation="horizontal" tagSummary={false} />
                {description && (
                    <Tooltip title={description} placement="bottom">
                        <QuestionCircleOutlined className="cursor-help text-gray-400" />
                    </Tooltip>
                )}
                {isPII && (
                    <Tooltip title="Masked data due to PII restriction">
                        <LockOutlined style={{ marginLeft: 8, color: "#1890ff" }} />
                    </Tooltip>
                )}
                {label}
            </div>
            {isAttachmentFieldByType || isAttachmentFieldByData ? (
                <AttachmentCell value={getAttachmentValue() as any} compact={true} showPreview={true} />
            ) : (
                <div
                    className={mergeClassNames(
                        "flex min-h-8 items-center rounded-md border border-solid bg-gray-50 px-2 py-1",
                        fieldError?.validateStatus === "error" ? "border-red-500" : "border-gray-300",
                    )}
                >
                    {isPhone ? <FormPhoneDisplayField value={getAttachmentValue() ?? ""} /> : children}
                </div>
            )}
            {fieldError?.validateStatus === "error" && <p className="text-sm text-red-500">{fieldError.help}</p>}
        </div>
    );
}
